import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home,
  ArrowLeft,
  Compass,
  GraduationCap,
  Layers,
  Mail,
  Sparkles,
  HelpCircle,
} from "lucide-react";
import { CompanyInfoContext } from "../../App";
import Button from "../common/Button";

const NoPage: React.FC = () => {
  const navigate = useNavigate();
  const companyInfo = useContext(CompanyInfoContext) || {};
  const currentYear = new Date().getFullYear();

  return (
    <div className="cht-min-h-screen cht-flex cht-flex-col cht-justify-between cht-bg-gradient-to-b cht-from-slate-50 cht-via-[#f2f4fd] cht-to-slate-100 cht-relative cht-overflow-hidden cht-font-body cht-text-[#1e293b] cht-selection:bg-[#3e57da]/20">
      {/* Decorative ambient background blur orbs */}
      <div className="cht-pointer-events-none cht-absolute -cht-top-32 -cht-left-32 cht-w-96 cht-h-96 cht-bg-[#3e57da]/15 cht-rounded-full cht-blur-3xl" />
      <div className="cht-pointer-events-none cht-absolute -cht-bottom-32 -cht-right-32 cht-w-96 cht-h-96 cht-bg-[#6366f1]/15 cht-rounded-full cht-blur-3xl" />
      <div className="cht-pointer-events-none cht-absolute cht-top-1/3 cht-right-1/4 cht-w-72 cht-h-72 cht-bg-[#3e57da]/5 cht-rounded-full cht-blur-2xl" />

      {/* Top minimal branding header */}
      <header className="cht-relative cht-z-10 cht-w-full cht-max-w-7xl cht-mx-auto cht-px-4 sm:cht-px-6 lg:cht-px-8 cht-py-6 cht-flex cht-items-center cht-justify-between">
        <Link
          to="/"
          className="cht-flex cht-items-center cht-gap-2.5 sm:cht-gap-3 cht-no-underline cht-group"
        >
          {companyInfo?.get_logo ? (
            <div className="cht-relative cht-w-10 cht-h-10 cht-rounded-xl cht-overflow-hidden cht-p-1.5 cht-bg-white/90 cht-shadow-sm cht-border cht-border-gray-200/60 group-hover:cht-scale-105 cht-transition-transform cht-duration-200">
              <img
                src={companyInfo.get_logo}
                alt={`${companyInfo?.company_name || "Company"}'s Logo`}
                className="cht-w-full cht-h-full cht-object-contain"
              />
            </div>
          ) : null}
          <div className="cht-leading-tight">
            <span className="cht-font-heading cht-font-black cht-text-sm sm:cht-text-base cht-text-[#1e293b] group-hover:cht-text-[#3e57da] cht-transition-colors">
              Codehub
            </span>
            <span className="cht-block cht-text-[9px] cht-font-bold cht-tracking-widest cht-uppercase cht-text-[#3e57da]">
              Technologies
            </span>
          </div>
        </Link>

        <Link
          to="/contact"
          className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-1.5 cht-rounded-xl cht-text-xs cht-font-bold cht-text-gray-600 hover:cht-text-[#3e57da] hover:cht-bg-white/80 cht-border cht-border-transparent hover:cht-border-gray-200/60 cht-transition-all cht-no-underline"
        >
          <HelpCircle size={14} />
          <span>Support</span>
        </Link>
      </header>

      {/* Center 404 Hero Card */}
      <main className="cht-relative cht-z-10 cht-flex-1 cht-flex cht-items-center cht-justify-center cht-px-4 sm:cht-px-6 cht-py-12">
        <div className="cht-w-full cht-max-w-2xl cht-bg-white/80 cht-backdrop-blur-2xl cht-border cht-border-gray-200/70 cht-rounded-[28px] cht-shadow-[0_20px_60px_rgba(0,0,0,0.06)] cht-p-8 sm:cht-p-12 cht-text-center cht-relative cht-overflow-hidden">
          {/* Subtle top card accent gradient bar */}
          <div className="cht-absolute cht-top-0 cht-left-0 cht-right-0 cht-h-1.5 cht-bg-gradient-to-r cht-from-[#3e57da] cht-via-[#6366f1] cht-to-[#3e57da]" />

          {/* Status badge pill */}
          <div className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-1 cht-rounded-full cht-text-xs cht-font-bold cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-border cht-border-[#3e57da]/20 cht-mb-4">
            <Sparkles size={13} className="cht-animate-pulse" />
            <span>Error 404 • Destination Offline</span>
          </div>

          {/* Giant Luminous 404 Display */}
          <div className="cht-relative cht-my-2">
            <h1 className="cht-font-heading cht-text-7xl sm:cht-text-9xl cht-font-black cht-tracking-tighter cht-bg-clip-text cht-text-transparent cht-bg-gradient-to-r cht-from-[#3e57da] cht-via-[#4f6bf5] cht-to-[#2036a1] cht-select-none cht-drop-shadow-sm">
              404
            </h1>
            <div className="cht-absolute cht-inset-0 cht-flex cht-items-center cht-justify-center cht-opacity-10 cht-pointer-events-none">
              <Compass
                size={160}
                className="cht-text-[#3e57da] cht-animate-spin"
                style={{ animationDuration: "30s" }}
              />
            </div>
          </div>

          {/* Content Headings */}
          <h2 className="cht-font-heading cht-text-2xl sm:cht-text-3xl cht-font-bold cht-text-[#1e293b] cht-mb-3 cht-tracking-tight">
            Lost in Digital Space?
          </h2>
          <p className="cht-text-gray-500 cht-text-sm sm:cht-text-base cht-max-w-md cht-mx-auto cht-mb-8 cht-leading-relaxed">
            The page you are looking for might have been relocated, renamed, or is temporarily unavailable. Let's guide you back to familiar grounds.
          </p>

          {/* Primary Navigation Actions */}
          <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-center cht-justify-center cht-gap-3 cht-mb-10">
            <Button
              to="/"
              variant="primary"
              size="md"
              leftIcon={<Home size={16} />}
              className="cht-w-full sm:cht-w-auto"
            >
              Go to Homepage
            </Button>

            <Button
              type="button"
              variant="secondary-outline"
              size="md"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft size={16} />}
              className="cht-w-full sm:cht-w-auto cht-bg-white hover:cht-bg-gray-50"
            >
              Go Back
            </Button>
          </div>

          {/* Quick Helpful Discovery Links */}
          <div className="cht-pt-8 cht-border-t cht-border-gray-100">
            <p className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider cht-mb-4">
              Or explore our key destinations
            </p>
            <div className="cht-flex cht-flex-wrap cht-items-center cht-justify-center cht-gap-2.5">
              <Link
                to="/courses"
                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-2 cht-rounded-xl cht-text-xs cht-font-semibold cht-bg-slate-100/80 hover:cht-bg-[#3e57da]/10 cht-text-gray-700 hover:cht-text-[#3e57da] cht-border cht-border-gray-200/50 hover:cht-border-[#3e57da]/20 cht-transition-all cht-no-underline"
              >
                <GraduationCap size={14} className="cht-text-[#3e57da]" />
                <span>Explore Courses</span>
              </Link>
              <Link
                to="/services"
                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-2 cht-rounded-xl cht-text-xs cht-font-semibold cht-bg-slate-100/80 hover:cht-bg-[#3e57da]/10 cht-text-gray-700 hover:cht-text-[#3e57da] cht-border cht-border-gray-200/50 hover:cht-border-[#3e57da]/20 cht-transition-all cht-no-underline"
              >
                <Layers size={14} className="cht-text-[#3e57da]" />
                <span>Our Services</span>
              </Link>
              <Link
                to="/contact"
                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-2 cht-rounded-xl cht-text-xs cht-font-semibold cht-bg-slate-100/80 hover:cht-bg-[#3e57da]/10 cht-text-gray-700 hover:cht-text-[#3e57da] cht-border cht-border-gray-200/50 hover:cht-border-[#3e57da]/20 cht-transition-all cht-no-underline"
              >
                <Mail size={14} className="cht-text-[#3e57da]" />
                <span>Contact Support</span>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="cht-relative cht-z-10 cht-w-full cht-py-6 cht-text-center cht-text-xs cht-text-gray-400">
        <p className="cht-mb-0">
          &copy; {currentYear} Codehub Technologies Ltd. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default NoPage;
